import React, { PropsWithChildren, ReactNode, Suspense, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { isMobile } from 'react-device-detect';
import {
    AppBar,
    Badge,
    Box,
    Divider,
    Drawer,
    IconButton,
    Link,
    List,
    MenuItem,
    styled,
    Toolbar,
    Typography,
} from '@mui/material';
import { AccountCircle, Chat, ChevronLeft } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar/AppBar';
import { ListItemLink } from 'components/mui/ListItemLink';
import SearchField from 'components/common/SearchField';
import useAuth from 'hooks/useAuth';
import useContextMenu from 'hooks/useContextMenu';
import { DividerModel, ModuleModel } from 'models/MenuItem';
import { CompanyType } from 'models/CompanyModel';
import { AuthState } from 'models/AuthModel';
import useCompanyModals from 'modules/companies/hooks/useCompanyModals';
import auth from 'recoil/atoms/auth';
import app from 'recoil/atoms/app';


const drawerWidth: number = 240;


type AppBarProps = MuiAppBarProps & {
    open?: boolean;
}

const StyledAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const StyledDrawer = styled(Drawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(7),
                },
            }),
        },
    }),
);

type AccountButtonProps = {
    handleProfileMenuOpen: (e: React.MouseEvent<HTMLElement>) => void,
}

const ChatButton = () => {
    const appState = useRecoilValue(app);

    return (
        <Link href="/chats" color="inherit">
            <IconButton color="inherit">
                <Badge badgeContent={appState.chatUnread} color="secondary">
                    <Chat />
                </Badge>
            </IconButton>
        </Link>
    );
};

const AccountButton = ({handleProfileMenuOpen}: AccountButtonProps) => {
    return (
        <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            color="inherit"
            onClick={handleProfileMenuOpen}
        >
            <AccountCircle />
        </IconButton>
    )
};

const DesktopTitle = ({hide}: {hide?: boolean}) => (
    <Typography
        component="div"
        variant="h6"
        color="inherit"
        noWrap
        mr={2}
        sx={{
            ...(hide && {display: 'none'}),
        }}
    >
        {process.env.REACT_APP_NAME}
    </Typography>
);


type DashboardInnerProps = PropsWithChildren<{
    open: boolean,
    toggle: () => void,
    menuItems: ReactNode,
    handleProfileMenuOpen: (e: React.MouseEvent<HTMLElement>) => void,
}>

const DashboardMobile = ({children, menuItems, open, toggle, handleProfileMenuOpen}: DashboardInnerProps) => {
    return (
        <>
            <AppBar
                position="fixed"
            >
                <Toolbar variant="dense">
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggle}
                        sx={{mr: 1}}
                    >
                        <MenuIcon />
                    </IconButton>

                    <SearchField />

                    <Box sx={{ flexGrow: 1 }} />

                    <Suspense fallback={null}>
                    <ChatButton />
                    </Suspense>

                    <AccountButton handleProfileMenuOpen={handleProfileMenuOpen} />
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                aria-label="main menu"
            >
                <Drawer
                    container={window.document.body}
                    variant="temporary"
                    open={open}
                    onClose={toggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                >
                    <Toolbar variant="dense">
                        {process.env.REACT_APP_NAME}
                    </Toolbar>

                    <Divider/>

                    {menuItems}
                </Drawer>
            </Box>

            {children}
        </>
    );
};

const DashboardDesktop = ({children, menuItems, open, toggle, handleProfileMenuOpen}: DashboardInnerProps) => {

    return (
        <>
            <StyledAppBar
                position="fixed"
                open={open}
            >
                <Toolbar variant="dense">
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggle}
                        sx={{
                            mr: 1,
                            ...(open && {display: 'none'}),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <DesktopTitle hide={open} />

                    <SearchField />

                    <Box sx={{ flexGrow: 1 }} />

                    <Suspense fallback={null}>
                    <ChatButton />
                    </Suspense>

                    <AccountButton handleProfileMenuOpen={handleProfileMenuOpen} />
                </Toolbar>
            </StyledAppBar>

            <StyledDrawer
                variant="permanent"
                open={open}
                aria-label="main menu"
            >
                <Toolbar
                    variant="dense"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: [1],
                    }}
                >
                    <DesktopTitle />

                    <IconButton onClick={toggle}>
                        <ChevronLeft/>
                    </IconButton>
                </Toolbar>

                <Divider/>

                {menuItems}
            </StyledDrawer>

            {children}
        </>
    );
};

type DashboardProps = {
    menuItems: (ModuleModel | DividerModel)[],
}

const DashboardLayout = ({ menuItems }: DashboardProps) => {
    const {t} = useTranslation();
    const { logout } = useAuth();
    const my = useRecoilValue(auth);
    const location = useLocation();
    const navigate = useNavigate();

    let drawerOpenDefault: string|null|boolean = localStorage.getItem('drawerOpen')
    if (drawerOpenDefault === null)
        drawerOpenDefault = window.innerWidth > 1640;
    else
        drawerOpenDefault = Boolean(Number(drawerOpenDefault));

    const [open, toggle] = useReducer(open => {
        const upd = !open;
        localStorage.setItem('drawerOpen', upd ? '1' : '0');
        return upd;
    }, drawerOpenDefault);
    const { openEditCompanyModal } = useCompanyModals(CompanyType.Shipper);

    const {openMenu} = useContextMenu(({close}) => [
        <MenuItem key="name" disabled>{my.lastName} {my.firstName}</MenuItem>,

        my.permissions.has('company update') && (
            <MenuItem
                key="company"
                onClick={() => {
                    openEditCompanyModal(my.company, true);
                    close();
                }}
            >
                {t('company')}
            </MenuItem>
        ),

        <MenuItem key="account" onClick={() => {navigate('/account');close();}}>{t('my_account')}</MenuItem>,

        my.permissions.has('settings update') && (
            <MenuItem key="settings" onClick={() => {navigate('/settings');close();}}>{t('settings')}</MenuItem>
        ),

        <MenuItem key="logout" onClick={logout}>{t('logout')}</MenuItem>,
    ]);

    useEffect(() => {
        if (isMobile && open) toggle();
        // eslint-disable-next-line
    }, [location.pathname]);

    if (my.state === AuthState.Guest) {
        return <Navigate to="/login" replace state={{ from: location.pathname }} />
    }

    const Inner = isMobile ? DashboardMobile : DashboardDesktop;

    const menu = (
        <List aria-label="main menu">
            {menuItems.map(m => 'divider' in m ?
                <Divider
                    key={m.divider}
                /> :
                (
                    (m.title && (!m.permission || my.permissions.has(m.permission))) ?
                        <ListItemLink
                            key={m.path}
                            to={m.path}
                            primary={t(m.title)}
                            icon={m.icon}
                            selected={(m.path.length > 2 && location.pathname.startsWith(m.path)) ||
                                m.path === location.pathname || // for dashboard
                                (m.subPages?.includes(location.pathname)) // for car history
                        }
                        /> :
                        null
                )
            )}
        </List>
    );

    return (
        <Box sx={{display: 'flex'}}>
            <Inner
                menuItems={menu}
                open={open}
                toggle={toggle}
                handleProfileMenuOpen={openMenu}
            >
                <Box
                    component="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                        scrollPaddingTop: 48,
                    }}
                >
                    <Toolbar variant="dense"/>

                    <Outlet />
                </Box>
            </Inner>
        </Box>
    )
};

export default DashboardLayout;
